import { PaymentHeaderDto } from '../../../invoices/dto/payment-header.dto';
import { Injectable } from '@angular/core';
import { ICreditCardService } from './icredit-card.service';
import { CreditCardPaymentInfo } from './credit-card-payment-info.model';
import { ApiService } from '../../../shared/api/api.service';
import { UtilService } from '../../../shared/util/util.service';
import { GatewayCustomerProfile } from './card-on-file/gateway-customer-profile.model';
import { CreditCardPaymentResult } from './credit-card-payment-result.model';
import { CreditCardTransactionDetail } from './credit-card-transaction-detail.model';
import { CreditCardTransactionSearch } from './credit-card-transaction-search.model';
import { CreditCardTransactionSearchResult } from './credit-card-transaction-search-result.model';
import { CreditCardRefundRequest } from './credit-card-refund-request.model';
import { CreditCardRefundResponse } from './credit-card-refund-response.model';
import { InvoiceLineItem } from '../invoice/invoice-lineitem.model';
import { getSafe } from 'src/app/shared/util/get-safe';
import { AdvancedPaymentInfoPaymentHeaderMapDto } from '../invoice-payment/dto/advanced-payment-info-payment-header-map.dto';
import { AdvancedCreditCardPaymentResult } from './advanced-credit-card-payment-result.model';
import { GatewayResolutionDto } from '../gateway-resolution/dto/gateway-resolution.dto';

export type CardOnFileResolution = { kind: 'fetch'; gatewayId: number } | { kind: 'none' };

/**
 * Decides which gateway's saved cards may be offered for a transaction, from that transaction's own gateway resolution.
 * An absent IsPreczn counts as non-Preczn. Rules, in order:
 *  - no gateways (null, undefined, empty or failed resolution): fetch with gateway 0
 *  - Preczn and non-Preczn gateways mixed: none
 *  - all Preczn and some items have no gateway: none (those items charge the tenant default, so it is mixed)
 *  - all Preczn: fetch with the lowest gateway id
 *  - all non-Preczn, exactly one gateway: fetch with that gateway id (even if some items have no gateway)
 *  - all non-Preczn, several gateways: fetch with gateway 0
 *
 * Callers must pass the result of their own resolveForRevenueItems call, never observeResolution().
 *
 * SYNC: keep in sync with the identical copy in the Portal app (src/app/revenue/shared/credit-card/credit-card.service.ts); change both together.
 */
export function classifyCardOnFileResolution(resolution: GatewayResolutionDto | null | undefined): CardOnFileResolution {
  const gateways = (resolution?.Gateways ?? []).filter(g => !!g);
  const unmapped = resolution?.ItemNumsWithoutGateway ?? [];
  const isPreczn = (g: { IsPreczn?: boolean }) => g.IsPreczn === true;

  if (gateways.length === 0) {
    return { kind: 'fetch', gatewayId: 0 };
  }
  const precznCount = gateways.filter(isPreczn).length;
  if (precznCount > 0 && precznCount < gateways.length) {
    return { kind: 'none' };
  }
  if (precznCount > 0) {
    return unmapped.length > 0
      ? { kind: 'none' }
      : { kind: 'fetch', gatewayId: Math.min(...gateways.map(g => g.CCGatewayID)) };
  }
  return { kind: 'fetch', gatewayId: gateways.length === 1 ? gateways[0].CCGatewayID : 0 };
}

type PaymentObjectType = {
  CC_ValidThrough: string;
  CC_NameOnCard: string;
  CC_BillingAddress1: string;
  CC_BillingAddress2: string;
  CC_BillingCity: string;
  CC_BillingState: string;
  CC_BillingZip: string;
  CC_BillingZipExt: string;
  CC_PhoneNum: string;
  CC_TypeOfCard?: string;
};

@Injectable({
  providedIn: 'root',
})
export class CreditCardService implements ICreditCardService {
  public _apiService: ApiService;
  public _utilService: UtilService;

  public cardTypeNone: string;
  public cardTypeAmex: string;
  public cardTypeDiscover: string;
  public cardTypeDinersClub: string;
  public cardTypeVisa: string;
  public cardTypeMastercard: string;

  public minCardLength: number;

  public constructor(apiService: ApiService, utilService: UtilService) {
    this._apiService = apiService;
    this._utilService = utilService;

    this.minCardLength = 14;

    this.cardTypeNone = 'none';
    this.cardTypeAmex = 'amex';
    this.cardTypeDiscover = 'discover';
    this.cardTypeDinersClub = 'dine';
    this.cardTypeVisa = 'visa';
    this.cardTypeMastercard = 'mastercard';
  }

  public fillPaymentHeaderFieldsWithCcInfo(creditCardInfo: CreditCardPaymentInfo, paymentHeader: PaymentHeaderDto) {
    paymentHeader.CC_ValidThrough = `${creditCardInfo.ValidThruMonth}/${creditCardInfo.ValidThruYear}`;
    paymentHeader.CC_NameOnCard = getSafe(() => creditCardInfo.CreditCardContactInfo.NameOnCard);
    paymentHeader.CC_BillingAddress1 = getSafe(() => creditCardInfo.CreditCardContactInfo.Address1);
    paymentHeader.CC_BillingAddress2 = getSafe(() => creditCardInfo.CreditCardContactInfo.Address2);
    paymentHeader.CC_BillingCity = getSafe(() => creditCardInfo.CreditCardContactInfo.City);
    paymentHeader.CC_BillingState = getSafe(() => creditCardInfo.CreditCardContactInfo.State);
    paymentHeader.CC_BillingZip = getSafe(() => creditCardInfo.CreditCardContactInfo.Zip);
    paymentHeader.CC_BillingZipExt = getSafe(() => creditCardInfo.CreditCardContactInfo.ZipExt);
    paymentHeader.CC_PhoneNum = getSafe(() => creditCardInfo.CreditCardContactInfo.Phone);
    paymentHeader.CC_TypeOfCard = creditCardInfo.CardType;
  }

  public fillCcFieldsWithCcInfo(creditCardInfo: CreditCardPaymentInfo, paymentObject: PaymentObjectType) {
    paymentObject.CC_ValidThrough = `${creditCardInfo.ValidThruMonth}/${creditCardInfo.ValidThruYear}`;
    paymentObject.CC_NameOnCard = creditCardInfo?.CreditCardContactInfo?.NameOnCard ?? '';
    paymentObject.CC_BillingAddress1 = creditCardInfo?.CreditCardContactInfo?.Address1 ?? '';
    paymentObject.CC_BillingAddress2 = creditCardInfo?.CreditCardContactInfo.Address2 ?? '';
    paymentObject.CC_BillingCity = creditCardInfo?.CreditCardContactInfo?.City ?? '';
    paymentObject.CC_BillingState = creditCardInfo?.CreditCardContactInfo?.State ?? '';
    paymentObject.CC_BillingZip = creditCardInfo?.CreditCardContactInfo?.Zip ?? '';
    paymentObject.CC_BillingZipExt = creditCardInfo?.CreditCardContactInfo?.ZipExt ?? '';
    paymentObject.CC_PhoneNum = creditCardInfo?.CreditCardContactInfo?.Phone ?? '';
    paymentObject.CC_TypeOfCard = creditCardInfo.CardType;
  }

  public cardTypeIsAccepted(cardName: string): boolean {
    return (
      cardName == this.cardTypeAmex ||
      cardName == this.cardTypeDiscover ||
      cardName == this.cardTypeVisa ||
      cardName == this.cardTypeMastercard
    );

    //cardName == this.cardTypeDinersClub ||
  }

  // information for various credit card types as a string map
  public _types: { [key: string]: any } = {
    amex: { pattern: /^3[47]/, length: 15 },
    discover: { pattern: /^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)/, length: 16 },
    dine: { pattern: /^36/, length: 14 },
    visa: { pattern: /^4/, length: 16 },
    mastercard: { pattern: /^5[1-5]/, length: 16 },
  };

  /**
   * Return the credit card type based on the card number (provided the card is in the accepted list of cards)
   *
   * @param cardNumber : string - Credit card number, which may include spaces or dashes, i.e. XXXX-YYYY-ZZZZ-ABCD
   *
   * @return string - Credit card (CCTypes member) type, which may be 'none' if the card number is not recognized
   */
  public getCardType(cardNumber: string): string {
    let cardType: string = 'none';

    if (this._utilService.isNotNullEmptyOrWhitespace(cardNumber)) {
      let key: string;
      let cardProps: any;
      let theCard: string = this._preprocess(cardNumber);

      for (key in this._types) {
        cardProps = this._types[key];

        if (theCard.match(cardProps['pattern'])) {
          cardType = key;
        }
      }
    }

    return cardType;
  }

  public getCardTypeDisplayName(cardType: string): string {
    let displayName: string = '';

    if (this._utilService.isNotNullEmptyOrWhitespace(cardType)) {
      switch (this._utilService.toLowerTrim(cardType)) {
        case this.cardTypeAmex:
          displayName = 'American Express';
          break;
        case this.cardTypeDinersClub:
          displayName = 'Diners Club International';
          break;
        case this.cardTypeDiscover:
          displayName = 'Discover';
          break;
        case this.cardTypeMastercard:
          displayName = 'Mastercard';
          break;
        case this.cardTypeVisa:
          displayName = 'Visa';
          break;
        default:
          displayName = '';
          break;
      }
    }

    return displayName;
  }

  /**
   * Is the supplied credit-card number valid
   *
   * @param cardNumber : string - Credit card number, which may include spaces or dashes, i.e. XXXX-YYYY-ZZZZ-ABCD
   *
   * @return boolean - true if the card number is recognized as a supported card type and the card-number properties are correct
   * for that card type.  Note that this does not mean the card is valid to be charged against, only that the number is theoretically
   * correct for the card type.
   */
  public isValid(cardNumber: string): boolean {
    let valid: boolean = false;
    if (this._utilService.isNotNullEmptyOrWhitespace(cardNumber)) {
      //OLD
      //let testNumber: string = this._preprocess(cardNumber);
      //let cardType: string = this.getCardType(testNumber);

      //if (cardType != this.cardTypeNone) {

      //valid = this._lengthValid(testNumber, cardType) && this._luhnValid(testNumber);

      //}

      //New to support new mastercard numbers for WC-1351 Update CC Number Validation on Event Registration
      // accept only digits, dashes or spaces
      if (/[^0-9-\s]+/.test(cardNumber)) return false;

      // The Luhn Algorithm. It's so pretty.
      var nCheck = 0,
        nDigit = 0,
        bEven = false;
      cardNumber = cardNumber.replace(/\D/g, '');

      for (var n = cardNumber.length - 1; n >= 0; n--) {
        var cDigit = cardNumber.charAt(n),
          nDigit = parseInt(cDigit, 10);

        if (bEven) {
          if ((nDigit *= 2) > 9) nDigit -= 9;
        }

        nCheck += nDigit;
        bEven = !bEven;
      }

      valid = nCheck % 10 == 0;
    }

    return valid;
  }

  // remove spaces and dashes that may be present in the credit card number
  public _preprocess(cardNumber: string): string {
    if (this._utilService.isNotNullEmptyOrWhitespace(cardNumber)) {
      return cardNumber.replace(/[ -]/g, '');
    } else {
      return '';
    }
  }

  // check the length of the credit card number based on its type
  public _lengthValid(cardNumber: string, cardType: string): boolean {
    let valid: boolean = false;

    let cardProps: any = this._types[cardType];
    if (this._utilService.isNotNullOrUndefined(cardProps)) {
      valid = cardProps ? cardNumber.length == cardProps['length'] : false;
    }

    return valid;
  }

  // check the credit card number with the Luhn algorithm
  public _luhnValid(cardNumber: string): boolean {
    let valid: boolean = false;
    if (this._utilService.isNotNullEmptyOrWhitespace(cardNumber)) {
      let digit: number;
      let n: number;
      let sum: number;
      let j: number;

      sum = 0;
      let numbers: Array<number> = cardNumber
        .split('')
        .reverse()
        .map((val) => parseFloat(val));
      let len: number = numbers.length;
      n = 0;
      j = 0;

      // there's really nothing new under the sun ...
      while (j < len) {
        digit = numbers[n];
        digit = +digit;

        if (n % 2) {
          digit *= 2;
          if (digit < 10) sum += digit;
          else sum += digit - 9;
        } else sum += digit;

        n = ++j;
      }

      valid = sum % 10 === 0;
    }

    return valid;
  }

  public hasCreditCardOnFileEnabled = (): Promise<boolean> => {
    let url = 'CardOnFile/CheckConfigSetting';
    return this._apiService.get(url);
  };

  /**
   * Prefer getGatewayCustomerProfileForResolution(profileId, resolution) for transaction screens.
   * Passing gatewayId 0 on a mixed tenant returns the default gateway's cards.
   */
  public getGatewayCustomerProfile = (profileId: number, gatewayId: number): Promise<GatewayCustomerProfile> => {
    let url = 'CardOnFile/CustomerProfile/' + profileId.toString() + '/' + gatewayId.toString();
    return this._apiService.get(url);
  };

  /**
   * Fetches saved cards for the gateway the transaction's resolution allows. When the resolution is mixed, returns an
   * empty profile without calling the API. The API response is returned unfiltered.
   */
  public getGatewayCustomerProfileForResolution(
    profileId: number,
    resolution: GatewayResolutionDto | null | undefined
  ): Promise<GatewayCustomerProfile> {
    if (!profileId) {
      return Promise.resolve(null);
    }
    const outcome = classifyCardOnFileResolution(resolution);
    if (outcome.kind === 'none') {
      return Promise.resolve(Object.assign(new GatewayCustomerProfile(), { ProfileId: profileId, PaymentProfiles: [] }));
    }
    return this.getGatewayCustomerProfile(profileId, outcome.gatewayId);
  }

  //process a card
  public processCreditCardPayment(creditCardPayment: CreditCardPaymentInfo): Promise<CreditCardPaymentResult> {
    return this._apiService.post('EPayment/ProcessCreditCardPayment', creditCardPayment);
  }
  public processAdvancedCreditCardPayments(
    creditcardPaymentHeaderMaps: Array<AdvancedPaymentInfoPaymentHeaderMapDto>
  ): Promise<Array<AdvancedCreditCardPaymentResult>> {
    return this._apiService.post('EPayment/ProcessCreditCardPayment/Advanced', creditcardPaymentHeaderMaps);
  }
  //refund a card transaction
  public refundCreditCardPayment(refundRequest: CreditCardRefundRequest): Promise<CreditCardRefundResponse> {
    return this._apiService.post('EPayment/RefundCreditCardPayment', refundRequest);
  }

  //search transactions
  public searchCreditCardPayments = (search: CreditCardTransactionSearch): Promise<CreditCardTransactionSearchResult> => {
    return this._apiService.getPaged('EPayment/CreditCardPayments', search);
  };

  // get invoice line items for a transaction
  public getTransactionInvoiceLineItems = (transactionId: number): Promise<InvoiceLineItem[]> => {
    return this._apiService.get('EPayment/CreditCardPayment/' + transactionId.toString() + '/InvoiceLineItems');
  };
}
